import { MantineSpacing, ScrollArea, StyleProp } from '@mantine/core';
import { Extension } from '@tiptap/core';
import Highlight from '@tiptap/extension-highlight';
import Placeholder from '@tiptap/extension-placeholder';
import { APP_ROUTES } from '@zorro/common-utils';
import { useDrawerFooter } from '@zorro/design-system/v2/Drawer';
import { NoCommentsIcon } from '@zorro/shared/assets';
import {
  Box,
  Group,
  Paper,
  Text,
  brand,
  useRichTextEditor,
} from '@zorro/web/design-system';
import {
  useImpersonation,
  useMonolithMutation,
  useMonolithQuery,
} from '@zorro/web/ui-utils';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Comment } from './Comment';
import { RichTextCommentEditor } from './RichTextCommentEditor';

export const CustomShortcuts = Extension.create({
  name: 'customShortcuts',

  addKeyboardShortcuts() {
    return {
      'Mod-Shift-x': () => this.editor.commands.toggleStrike(),
      'Mod-h': () => {
        const isActive = this.editor.isActive('highlight');
        return isActive
          ? this.editor.commands.unsetMark('highlight')
          : this.editor.commands.setMark('highlight');
      },
    };
  },
});

type Props = {
  employerId: string;
  employeeId: string;
  onboardingPeriodId: string;
  selectedCommentId: string | null;
  px?: StyleProp<MantineSpacing>;
  editorPy?: StyleProp<MantineSpacing>;
  sendButtonAlign?: 'bottom' | 'center';
};

export const Comments = ({
  employerId,
  employeeId,
  onboardingPeriodId,
  selectedCommentId,
  px = 'lg',
  editorPy = 'lg',
  sendButtonAlign = 'bottom',
}: Props) => {
  const viewport = useRef<HTMLDivElement>(null);
  const editorViewport = useRef<HTMLDivElement>(null);

  const { data: comments } = useMonolithQuery({
    method: 'onboardingPeriodsControllerFindManyEnrollmentComments',
    params: [onboardingPeriodId],
  });

  const { tryMutate: createComment } = useMonolithMutation({
    method: 'onboardingPeriodsControllerCreateEnrollmentComment',
    shouldReloadPage: false,
    shouldShowLoadingOverlay: false,
  });

  const [content, setContent] = useState<string>('');
  const { user } = useImpersonation();
  const { editor } = useRichTextEditor(
    {
      extensions: [
        Placeholder.configure({ placeholder: 'Add a comment…' }),
        Highlight,
        CustomShortcuts,
      ],
      content: '',
      onUpdate({ editor }) {
        const content = editor.isEmpty ? {} : editor.getJSON();
        setContent(JSON.stringify(content));
      },
    },
    []
  );

  useEffect(() => {
    if (!viewport.current) return;
    viewport.current.scrollTo({
      top: viewport.current.scrollHeight,
      behavior: 'smooth',
    });
  }, [comments?.length]);

  const handleCreateComment = async () => {
    if (!content || !user?.id || !onboardingPeriodId) return;

    if (!editor?.getText().trim()) return;

    const createResult = await createComment([
      onboardingPeriodId,
      {
        content: JSON.stringify(editor.getJSON()),
        onboardingPeriodId,
        performedById: user?.id,
        employeeId,
        employerId,
      },
    ]);

    if (createResult.isOk()) {
      setContent('');
      editor?.commands.setContent('');
    }
  };

  const handleCopyLink = async (id: string) => {
    const link = `${window.location.origin}${APP_ROUTES.OPS_EMPLOYEE_PLANS_TAB({
      employerId,
      employeeId,
      onboardingPeriodId,
    })}?commentId=${id}`;

    await navigator.clipboard.writeText(link);

    return link;
  };

  // Stable wrapper so the memoized footer doesn't re-register every render
  // (handleCreateComment is recreated on each render).
  const handleCreateCommentRef = useRef(handleCreateComment);
  handleCreateCommentRef.current = handleCreateComment;
  const handleSubmitComment = useCallback(
    () => handleCreateCommentRef.current(),
    []
  );

  // When rendered inside the V2 Drawer, pin the editor to the drawer's footer
  // slot. The footer sits outside the body's horizontal padding, so the editor
  // spans the full drawer width and the scroll area shrinks above it (no
  // overlap). Outside a drawer (e.g. the popover) it falls back to inline.
  const drawerFooter = useMemo(
    () => (
      <RichTextCommentEditor
        editor={editor}
        onClick={handleSubmitComment}
        content={content}
        viewport={editorViewport}
      />
    ),
    [editor, handleSubmitComment, content]
  );
  const isRenderedInDrawerFooter = useDrawerFooter(drawerFooter);

  return (
    <Box
      h="100%"
      style={{ flex: 1, display: 'flex', flexDirection: 'column' }}
      p={0}
    >
      {comments?.length === 0 && (
        <Group
          wrap="nowrap"
          justify="center"
          align="center"
          pb="6rem"
          flex={1}
          style={{ flexDirection: 'column' }}
          gap={0}
        >
          <NoCommentsIcon />
          <Text fw={600} mt={16}>
            No comments yet
          </Text>
          <Text c={brand.zorroGray400}>
            Share and document progress, issues, and further requests
          </Text>
        </Group>
      )}
      <ScrollArea
        w="100%"
        viewportRef={viewport}
        mih={0}
        style={{ flex: comments?.length === 0 ? 0 : 1 }}
      >
        {comments?.map((comment) => (
          <Comment
            employeeId={employeeId}
            employerId={employerId}
            key={comment.id}
            id={comment.id}
            performedByFirstName={comment.performedByFirstName ?? ''}
            performedByLastName={comment.performedByLastName ?? ''}
            createdAt={comment.createdAt}
            updatedAt={comment?.updatedAt}
            content={comment.content}
            onboardingPeriodId={comment.onboardingPeriodId}
            performedById={comment.performedById}
            isSelected={
              selectedCommentId ? comment.id === selectedCommentId : undefined
            }
            onCopyLink={handleCopyLink}
          />
        ))}
      </ScrollArea>

      {!isRenderedInDrawerFooter && (
        <Paper
          shadow="0px -4px 24.6px 0px rgba(0, 0, 0, 0.04)"
          w="100%"
          py={editorPy}
          px={px}
          display="flex"
          style={{ flexShrink: 0 }}
        >
          <RichTextCommentEditor
            editor={editor}
            onClick={handleCreateComment}
            content={content}
            viewport={editorViewport}
            sendButtonAlign={sendButtonAlign}
          />
        </Paper>
      )}
    </Box>
  );
};
